"use client";

import { useState } from "react";
import { Copy, Check, Eye, Code, Maximize2, X, Download } from "lucide-react";

interface CodeBlockProps {
  node: any;
  inline: boolean;
  className: string;
  children: any;
}

export function CodeBlock({
  node,
  inline,
  className,
  children,
  ...props
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<"code" | "preview">("preview");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const codeText = String(children).replace(/\n$/, "");

  const isHtml =
    className?.includes("language-html") ||
    codeText.trim().startsWith("<!DOCTYPE") ||
    codeText.trim().startsWith("<html");

  if (!inline) {
    return (
      <>
        <div className="not-prose relative mb-4">
          {/* TOP RIGHT ICONS */}
          <div className="absolute top-3 right-3 flex gap-2 z-10">
            {isHtml && (
              <button
                onClick={() =>
                  setViewMode(viewMode === "code" ? "preview" : "code")
                }
                className="bg-zinc-800 text-white p-1.5 rounded-md hover:bg-zinc-700 transition"
              >
                {viewMode === "code" ? <Eye size={16} /> : <Code size={16} />}
              </button>
            )}

            {viewMode === "code" && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(codeText);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="bg-zinc-800 text-white p-1.5 rounded-md hover:bg-zinc-700 transition"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            )}

            {viewMode === "preview" && (
              <>
                <button
                  onClick={() => setIsFullscreen(true)}
                  className="bg-zinc-800 text-white p-1.5 rounded-md hover:bg-zinc-700 transition"
                >
                  <Maximize2 size={16} />
                </button>
               <button
                  onClick={() => {
                    const printWindow = window.open("", "_blank");

                    if (!printWindow) return;

                    printWindow.document.open();
                    printWindow.document.write(`
                              <html>
                              <head>
                                <style>
                                  @page { margin: 0; }
                                  body { margin: 0; }
                                  @media print {
                                    body { margin: 0; }
                                  }
                                </style>
                              </head>
                              <body>
                                ${codeText}
                                <script>
                                  window.onload = function() {
                                    window.print();
                                  };

                                  window.onafterprint = function() {
                                    window.close();
                                  };
                                <\/script>
                              </body>
                              </html>
                              `);
                    printWindow.document.close();
                  }}
                  className="bg-zinc-800 text-white p-1.5 rounded-md hover:bg-zinc-700 transition"
                  title="Download Preview"
                >
                  <Download size={16} />
                </button>
              </>
            )}
          </div>

          {/* CONTENT SWITCH */}
          {viewMode === "code" ? (
            <pre
              {...props}
              className="text-sm w-full overflow-x-auto dark:bg-zinc-900 p-4 border border-zinc-200 dark:border-zinc-700 rounded-xl dark:text-zinc-50 text-zinc-900 max-sm:break-all"
            >
              <code className="whitespace-pre-wrap break-words">
                {codeText}
              </code>
            </pre>
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <iframe
                srcDoc={codeText}
                title="HTML Preview"
                className="w-full h-[450px] bg-white"
                sandbox=""
              />
            </div>
          )}
        </div>

        {/* 🔥 FULLSCREEN OVERLAY */}
        {isFullscreen && (
          <div className="fixed inset-0 bg-black/70 z-50 flex flex-col">
            {/* Top Bar */}
            <div className="flex justify-between items-center p-4 bg-zinc-900 text-white">
              <span className="text-sm">Preview Mode</span>
              <button
                onClick={() => setIsFullscreen(false)}
                className="p-2 hover:bg-zinc-700 rounded-md"
              >
                <X size={20} />
              </button>
            </div>

            {/* Fullscreen Iframe */}
            <iframe
              srcDoc={codeText}
              title="Fullscreen HTML Preview"
              className="flex-1 w-full bg-white"
              sandbox=""
            />
          </div>
        )}
      </>
    );
  }

  return (
    <code
      className={`${className} text-sm bg-zinc-100 dark:bg-zinc-800 py-0.5 px-1 rounded-md`}
      {...props}
    >
      {children}
    </code>
  );
}
